/**
 * TusDirectUpload — real implementation of TusDirectUploadProvider.
 *
 * When tusUploadEnabled is true on the backend, this extension delegates to
 * the shared TUS helper:
 * 1. Uploads the raw File object directly via tus-js-client (no SHA, no arrayBuffer)
 * 2. Backend computes SHA-256 (native crypto, streaming) and returns it via X-Blob-Key header
 * 3. Warms OPFS with the uploaded file and returns the server-computed blob key
 *
 * When tus is disabled, upload() returns null. Streamable callers must fail
 * instead of using a BlobEngine full-buffer fallback.
 */
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { WorkspaceBlobUploadOutboxService } from '@affine/core/modules/offline-mutation-outbox';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { uploadWorkspaceFileViaTus } from '@affine/core/modules/workspace/utils/tus-direct-upload';
import type { Container } from '@blocksuite/affine/global/di';
import {
  type ITusDirectUploadService,
  TusDirectUploadProvider,
  type UploadProgressCallback,
} from '@blocksuite/affine/shared/services';
import { Extension } from '@blocksuite/affine/store';
import type { FrameworkProvider } from '@toeverything/infra';

export function patchTusDirectUploadExtension(framework: FrameworkProvider) {
  const workspaceService = framework.get(WorkspaceService);
  const workspaceServerService = framework.get(WorkspaceServerService);
  const blobUploadOutbox = framework.get(WorkspaceBlobUploadOutboxService);
  const configuredServerBaseUrl =
    workspaceServerService.server?.baseUrl ?? location.origin;
  const workspaceId = workspaceService.workspace.id;

  class StreamLainTusDirectUploadService
    extends Extension
    implements ITusDirectUploadService
  {
    async stage(
      file: File,
      onProgress?: UploadProgressCallback,
      docId?: string
    ): Promise<string> {
      return blobUploadOutbox.stage(file, { docId, onProgress });
    }

    async upload(
      file: File,
      onProgress?: UploadProgressCallback,
      docId?: string
    ): Promise<string | null> {
      return uploadWorkspaceFileViaTus({
        workspaceId,
        file,
        onProgress,
        docId,
        serverBaseUrl: configuredServerBaseUrl,
      });
    }

    static override setup(di: Container) {
      di.override(TusDirectUploadProvider, StreamLainTusDirectUploadService);
    }
  }

  return StreamLainTusDirectUploadService;
}
